import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Moon,
  Sun,
  Search,
  Coins,
  DollarSign,
  LogOut,
  User,
  Settings,
  Shield,
  HelpCircle,
  TrendingUp,
  Home,
  Compass,
  Target,
  Users,
  Sparkles,
  MessageCircle,
  Clock,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import FeedbackWidget from "@/components/FeedbackWidget";
import { t } from "@/i18n";

function daysLeftInTrial(user) {
  if (!user || user.account_type !== "trial") return null;
  const days = user.access_days_remaining;
  if (typeof days !== "number") return null;
  return Math.max(0, days);
}

const NAV_ITEMS = [
  {
    to: "/dashboard",
    label: t("navbar.accueil"),
    icon: Home,
    testId: "nav-dashboard",
  },
  {
    to: "/roadmap",
    label: t("navbar.apprendre"),
    icon: Compass,
    testId: "nav-roadmap",
  },
  {
    to: "/practice",
    label: t("navbar.s_entrainer"),
    icon: Target,
    testId: "nav-practice",
  },
  {
    to: "/progress",
    label: t("navbar.progres"),
    icon: TrendingUp,
    testId: "nav-progress",
  },
  {
    to: "/recompenses",
    label: t("navbar.mon_oasis"),
    icon: Sparkles,
    testId: "oasis-link",
  },
];

const GLASS =
  "rounded-full bg-background/70 backdrop-blur-xl border border-border/60 shadow-sm";

export default function Navbar() {
  const { dark, toggle } = useTheme();
  const { user, updateDinars, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [menu, setMenu] = useState(false);
  const [dinars, setDinars] = useState(user?.dinars ?? 0);
  const [feedback, setFeedback] = useState(false);

  const menuRefMobile = useRef();
  const menuRefDesktop = useRef();
  const userId = user?.id;
  const trialDaysLeft = daysLeftInTrial(user);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--trial-banner-h",
      trialDaysLeft !== null ? "40px" : "0px",
    );
  }, [trialDaysLeft]);

  useEffect(() => {
    setShowSearch(false);
    setMenu(false);
  }, [loc.pathname]);

  useEffect(() => {
    setDinars(user?.dinars ?? 0);
  }, [user?.dinars]);

  useEffect(() => {
    const refreshDinars = async () => {
      if (!userId) {
        setDinars(0);
        return;
      }
      try {
        const { data } = await api.get("/recompenses");
        const balance = data.balance ?? 0;
        setDinars(balance);
        updateDinars(balance);
      } catch {}
    };
    refreshDinars();
    const onDinarUpdate = (event) => {
      const balance = event.detail?.dinars_balance ?? event.detail?.balance;
      if (typeof balance === "number") {
        setDinars(balance);
        updateDinars(balance);
      } else {
        refreshDinars();
      }
    };
    window.addEventListener("arabdex:dinar-update", onDinarUpdate);
    return () =>
      window.removeEventListener("arabdex:dinar-update", onDinarUpdate);
  }, [userId, updateDinars]);

  useEffect(() => {
    const h = (e) => {
      const inMobile = menuRefMobile.current?.contains(e.target);
      const inDesktop = menuRefDesktop.current?.contains(e.target);
      if (!inMobile && !inDesktop) setMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setRes([]);
      return;
    }
    const tId = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setRes(data);
      } catch {}
    }, 250);
    return () => clearTimeout(tId);
  }, [q]);

  const go = (r) => {
    setShowSearch(false);
    setQ("");
    if (r.type === "lesson") nav(`/learn/lesson/${r.id}`);
    if (r.type === "reading") nav(`/practice/reading/${r.id}`);
  };

  const active = (base) => loc.pathname.startsWith(base);
  const replayTutorial = () => {
    localStorage.setItem("replayTutorial", "1");
    setMenu(false);
    nav("/dashboard");
    window.location.reload();
  };

  const items = [
    ...NAV_ITEMS,
    ...(user?.is_affiliate
      ? [
          {
            to: "/affiliate",
            label: t("navbar.mon_espace_affilie"),
            icon: Users,
            testId: "nav-affiliate",
          },
        ]
      : []),
  ];

  const AvatarButton = ({ testId }) => (
    <button
      data-testid={testId}
      onClick={() => setMenu((m) => !m)}
      className={`w-9 h-9 flex items-center justify-center overflow-hidden shrink-0 text-primary font-semibold ${GLASS}`}
      title={t("navbar.mon_compte")}
      aria-label={t("navbar.ouvrir_mon_compte")}
    >
      {user?.picture ? (
        <img src={user.picture} alt="" className="w-full h-full rounded-full" />
      ) : (
        user?.name?.[0]?.toUpperCase() || <User size={16} />
      )}
    </button>
  );

  const TrialPillMobile = () =>
    trialDaysLeft === null ? null : (
      <Link
        to="/pricing"
        data-testid="trial-badge-mobile"
        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-500/15 text-rose-600 dark:text-rose-400 shrink-0 text-[11px] font-semibold whitespace-nowrap"
        title={t("navbar.voir_les_formules")}
      >
        <Clock size={12} />
        {trialDaysLeft}j
      </Link>
    );

  const TrialBannerDesktop = () =>
    trialDaysLeft === null ? null : (
      <div
        className="hidden md:flex fixed inset-x-0 z-30 justify-center items-center bg-rose-50/90 dark:bg-rose-500/10 backdrop-blur-sm border-b border-rose-200/70 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs font-medium"
        style={{ top: "68px", height: "40px" }}
      >
        <span>
          {trialDaysLeft > 0
            ? t("navbar.nb_jours_restants_essai", {
                count: trialDaysLeft,
                s: trialDaysLeft > 1 ? "s" : "",
              })
            : t("navbar.your_free_trial_ends_today")}
        </span>
        <Link
          to="/pricing"
          className="ml-3 font-semibold underline underline-offset-2 hover:text-rose-900 dark:hover:text-rose-100 transition"
        >
          {t("navbar.subscribe")}
        </Link>
      </div>
    );

  const AccountMenu = () => (
    <div className="absolute right-0 mt-2 w-52 rounded-2xl border bg-popover shadow-lg p-1 z-50">
      <div className="px-2 py-1.5 text-[11px] text-muted-foreground truncate border-b mb-0.5">
        {user?.email}
      </div>

      <Link
        to="/affiliate-program"
        onClick={() => setMenu(false)}
        className="sm:hidden w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
      >
        <DollarSign size={14} /> {t("navbar.devenir_affilie")}
      </Link>
      {user?.is_affiliate && (
        <button
          data-testid="menu-affiliate"
          onClick={() => {
            setMenu(false);
            nav("/affiliate");
          }}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
        >
          <Users size={14} /> {t("navbar.mon_espace_affilie")}
        </button>
      )}
      <button
        data-testid="menu-account"
        onClick={() => {
          setMenu(false);
          nav("/account");
        }}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
      >
        <Settings size={14} /> {t("navbar.mon_compte")}
      </button>

      {user?.role === "admin" && (
        <button
          data-testid="menu-admin"
          onClick={() => {
            setMenu(false);
            nav("/admin");
          }}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
        >
          <Shield size={14} /> {t("navbar.espace_admin")}
        </button>
      )}
      <button
        data-testid="menu-tutorial"
        onClick={replayTutorial}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
      >
        <HelpCircle size={14} /> {t("navbar.revoir_le_tutoriel")}
      </button>
      <button
        data-testid="theme-toggle"
        onClick={toggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition"
      >
        {dark ? <Sun size={14} /> : <Moon size={14} />}
        {dark ? t("navbar.mode_clair") : t("navbar.mode_nuit")}
      </button>
      <button
        data-testid="logout-btn"
        onMouseDown={(e) => {
          e.preventDefault();
          setMenu(false);
          logout();
        }}
        onClick={(e) => e.preventDefault()}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-secondary transition text-destructive border-t mt-0.5 pt-1.5"
      >
        <LogOut size={14} /> {t("navbar.deconnexion")}
      </button>
    </div>
  );

  return (
    <>
      {/* ---------- Mobile (< md) : barre pleine largeur classique ---------- */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-16 border-b bg-background/80 backdrop-blur-xl">
        <div className="h-16 px-2 flex items-center gap-2">
          <Link
            to="/dashboard"
            data-testid="logo"
            className="flex items-center gap-1.5 shrink-0"
          >
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-head font-bold arabic">
              ع
            </div>
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              data-testid="search-btn-mobile"
              onClick={() => setShowSearch((s) => !s)}
              className="p-1.5 rounded-lg hover:bg-secondary transition"
              aria-label={t("navbar.rechercher")}
            >
              <Search size={16} />
            </button>

            <button
              data-testid="feedback-btn-mobile"
              onClick={() => setFeedback((f) => !f)}
              className="p-1.5 rounded-lg hover:bg-secondary transition"
              aria-label={t("navbar.un_probleme_une_idee")}
              title={t("navbar.un_probleme_une_idee")}
            >
              <MessageCircle size={16} />
            </button>
            <TrialPillMobile />
            <Link
              to="/recompenses"
              data-testid="dinar-badge-mobile"
              className="flex items-center gap-0.5 px-1.5 py-1 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0"
            >
              <Coins size={15} />
              <span className="text-xs font-semibold">{dinars}</span>
            </Link>
          </div>
          <div className="relative shrink-0" ref={menuRefMobile}>
            <button
              data-testid="avatar-btn-mobile"
              onClick={() => setMenu((m) => !m)}
              className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold overflow-hidden shrink-0"
              aria-label={t("navbar.ouvrir_mon_compte")}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-full h-full rounded-full"
                />
              ) : (
                user?.name?.[0]?.toUpperCase() || <User size={16} />
              )}
            </button>
            {menu && <AccountMenu />}
          </div>
        </div>
        {showSearch && (
          <div className="absolute top-16 inset-x-0 border-b bg-background p-3">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("navbar.rechercher_2")}
              className="w-full px-4 py-3 rounded-xl border bg-card outline-none focus:ring-2 ring-primary"
            />
            {res.length > 0 && (
              <div className="mt-2 rounded-xl border bg-card divide-y">
                {res.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => go(r)}
                    className="w-full text-left px-4 py-2.5 hover:bg-secondary transition flex justify-between"
                  >
                    <span className="font-medium text-sm">{r.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {r.sub}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ---------- Desktop (md+) : bulles flottantes translucides ---------- */}
      <header className="hidden md:block fixed top-0 inset-x-0 z-40 pointer-events-none">
        <style>{`
          .harf-nav-pill::-webkit-scrollbar { display: none; }
          .harf-nav-pill { scrollbar-width: none; -ms-overflow-style: none; }
        `}</style>
        <div className="max-w-7xl mx-auto px-6 pt-4 flex items-center justify-between gap-3 min-w-0">
          <Link
            to="/dashboard"
            data-testid="logo"
            className={`pointer-events-auto flex items-center gap-2 shrink-0 px-3.5 py-2 ${GLASS}`}
          >
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-head font-bold arabic text-sm">
              ع
            </div>
            <span className="font-head font-extrabold text-base">
              {t("navbar.harf")}
            </span>
          </Link>
          <nav
            aria-label={t("navbar.navigation_principale")}
            className={`harf-nav-pill pointer-events-auto flex items-center gap-1 px-1.5 py-1.5 min-w-0 overflow-x-auto ${GLASS}`}
          >
            {items.map(({ to, label, icon: Icon, testId }) => (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                title={label}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                  active(to)
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`}
              >
                <Icon size={15} className="shrink-0" />
                {label}
              </Link>
            ))}
          </nav>
          <div className="pointer-events-auto flex items-center gap-2 shrink-0">
            <div className={`flex items-center gap-0.5 px-1.5 py-1.5 ${GLASS}`}>
              <button
                data-testid="search-btn"
                onClick={() => setShowSearch((s) => !s)}
                className="p-1.5 rounded-full hover:bg-secondary/70 transition"
                title={t("navbar.rechercher")}
                aria-label={t("navbar.rechercher")}
              >
                <Search size={16} />
              </button>
              <button
                data-testid="feedback-btn"
                onClick={() => setFeedback((f) => !f)}
                className="p-1.5 rounded-full hover:bg-secondary/70 transition"
                title={t("navbar.un_probleme_une_idee")}
                aria-label={t("navbar.un_probleme_une_idee")}
              >
                <MessageCircle size={16} />
              </button>
              <Link
                to="/affiliate-program"
                data-testid="nav-affiliate-program"
                className={`p-1.5 rounded-full transition ${active("/affiliate-program") ? "bg-primary text-primary-foreground" : "hover:bg-secondary/70"}`}
                title={t("navbar.devenir_affilie")}
                aria-label={t("navbar.devenir_affilie")}
              >
                <DollarSign size={16} />
              </Link>
            </div>
            <Link
              to="/recompenses"
              data-testid="dinar-badge"
              title={t("navbar.mes_recompenses")}
              className={`flex items-center gap-1 px-3 py-2 text-amber-600 dark:text-amber-400 hover:brightness-95 transition shrink-0 ${GLASS}`}
            >
              <Coins size={15} />
              <span className="text-sm font-semibold">{dinars}</span>
            </Link>
            <div className="relative" ref={menuRefDesktop}>
              <AvatarButton testId="avatar-btn" />
              {menu && <AccountMenu />}
            </div>
          </div>
        </div>
        {showSearch && (
          <div className="pointer-events-auto max-w-2xl mx-auto mt-2 px-6">
            <div className={`p-3 ${GLASS} rounded-3xl`}>
              <input
                autoFocus
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("navbar.rechercher_2")}
                className="w-full px-4 py-3 rounded-2xl border bg-card outline-none focus:ring-2 ring-primary"
              />
              {res.length > 0 && (
                <div className="mt-2 rounded-2xl border bg-card divide-y overflow-hidden">
                  {res.map((r, i) => (
                    <button
                      key={i}
                      data-testid={`search-result-${i}`}
                      onClick={() => go(r)}
                      className="w-full text-left px-4 py-2.5 hover:bg-secondary transition flex justify-between"
                    >
                      <span className="font-medium text-sm">{r.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {r.sub}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <TrialBannerDesktop />

      {/* ---------- Mobile (< md) : barre d'onglets fixe en bas ---------- */}
      <nav
        aria-label={t("navbar.navigation_mobile")}
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-stretch">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testId }) => (
            <Link
              key={to}
              to={to}
              data-testid={`mobile-${testId}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-w-0 transition ${
                active(to) ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <Icon size={20} strokeWidth={active(to) ? 2.5 : 2} />
              <span className="text-[10px] font-semibold leading-none truncate max-w-full px-0.5">
                {label}
              </span>
            </Link>
          ))}
        </div>
      </nav>

      <FeedbackWidget open={feedback} onOpenChange={setFeedback} />
    </>
  );
}
